import React, { useState } from 'react';
import {
  Box, Button, Typography,
} from '@mui/material';
import { useStatusMessageDispatch } from '../../../hooks/StatusMessageContext';
import { useTranslation } from 'react-i18next';

interface SignerInviteProps {
  signerEmail?: string;
  isSignerVerified?: boolean;
  onInvite: (email: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
}

export const SignerInvite: React.FC<SignerInviteProps> = ({
  signerEmail = '',
  isSignerVerified = false,
  onInvite,
  onCancel,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'survey-questions.signaturepad-with-invite' });
  const { setStatusMessage } = useStatusMessageDispatch();
  const [ email, setEmail ] = useState(signerEmail);
  const [ hasSigner, setHasSigner ] = useState(Boolean(signerEmail));
  const [ loading, setLoading ] = useState(false);

  const handleInviteClick = async () => {
    setLoading(true);

    try {
      await onInvite(email.trim());
      setHasSigner(true);
      setStatusMessage({
        message: t('invite_sent'),
        severity: 'success',
      });
    } catch (error) {
      setHasSigner(false);
      setStatusMessage({
        message: t('invite_error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = async () => {
    setLoading(true);

    try {
      await onCancel();
      setEmail('');
      setHasSigner(false);
      setStatusMessage({
        message: t('invite_canceled'),
        severity: 'success',
      });
    } catch {
      setHasSigner(true);
      setStatusMessage({
        message: t('cancel_error'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        padding: 2,
        marginBottom: 3,
        border: '1px solid #d4d4d4',
        borderRadius: 1,
        maxWidth: '600px', // Match the signature pad's max width
        width: '100%',
      }}
    >
      <Typography
        sx={{
          marginBottom: 1,
          fontWeight: 600,
          whiteSpace: 'pre-wrap',
        }}
        variant='body1'
      >
        {t('invite_header')}
      </Typography>
      {!isSignerVerified && (
        <Typography
          sx={{
            marginBottom: 1,
            whiteSpace: 'pre-wrap',
          }}
          variant='body2'
        >
          {t('invite_intro')}
          <em>{t('invite_signer')}</em>
          {'. '}
          {t('invite_outro')}
        </Typography>
      )}
      {isSignerVerified && (
        <Typography
          sx={{
            marginBottom: 1,
            whiteSpace: 'pre-wrap',
          }}
          variant='body2'
        >
          {t('signer_verified_disclaimer')}
        </Typography>
      )}
      <Box
        alignItems='center'
        display='flex'
        gap={2}
      >
        <input
          required
          className={hasSigner ? 'sd-input sd-text sd-input--readonly' : 'sd-input sd-text'}
          placeholder={t('email_placeholder')}
          readOnly={hasSigner || loading}
          title=''
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          color='primary'
          disabled={loading || !email.trim() || isSignerVerified}
          sx={{
            padding: '4px 12px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            lineHeight: 2.2,
          }}
          variant='contained'
          onClick={hasSigner ? handleCancelClick : handleInviteClick}
        >
          {loading ? t('processing') : (hasSigner ? t('cancel_invitation') : t('invite_signer'))}
        </Button>
      </Box>
      <Typography
        sx={{
          marginTop: 2,
          whiteSpace: 'pre-wrap',
        }}
        variant='body2'
      >
        <em>
          <strong>{t('note_label')}</strong>
          {' '}
          {t('note_message')}
        </em>
      </Typography>
    </Box>
  );
};
